/**
 * Convert hex color to rgba with opacity
 * @param hex - Hex color string (e.g., "#7C3AED")
 * @param alpha - Opacity value between 0 and 1
 * @returns RGBA color string
 */
export function hexToRgba(hex: string, alpha: number = 1): string {
  // Remove the hash if present
  const cleanHex = hex.replace('#', '');
  
  // Parse the hex values
  const r = parseInt(cleanHex.substring(0, 2), 16);
  const g = parseInt(cleanHex.substring(2, 4), 16);
  const b = parseInt(cleanHex.substring(4, 6), 16);
  
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

/**
 * Calculate the luminance of a color
 * @param hex - Hex color string
 * @returns Luminance value between 0 and 1
 */
export function getLuminance(hex: string): number {
  // Remove the hash if present
  const cleanHex = hex.replace('#', '');
  
  // Parse the hex values
  const r = parseInt(cleanHex.substring(0, 2), 16) / 255;
  const g = parseInt(cleanHex.substring(2, 4), 16) / 255;
  const b = parseInt(cleanHex.substring(4, 6), 16) / 255;
  
  // Apply gamma correction
  const gammaCorrect = (value: number) => {
    return value <= 0.03928 ? value / 12.92 : Math.pow((value + 0.055) / 1.055, 2.4);
  };
  
  const rCorrected = gammaCorrect(r);
  const gCorrected = gammaCorrect(g);
  const bCorrected = gammaCorrect(b);
  
  // Calculate relative luminance
  return 0.2126 * rCorrected + 0.7152 * gCorrected + 0.0722 * bCorrected;
}

/**
 * Determine if a color is light or dark
 * @param hex - Hex color string
 * @returns true if the color is light, false if dark
 */
export function isLightColor(hex: string): boolean {
  return getLuminance(hex) > 0.5;
}

/**
 * Get a contrasting text color (black or white) based on background
 * @param bgHex - Background hex color string
 * @returns "#000000" for light backgrounds, "#FFFFFF" for dark backgrounds
 */
export function getContrastingTextColor(bgHex: string): string {
  return isLightColor(bgHex) ? '#000000' : '#FFFFFF';
}